/**
 * Utilitaires pour traduire les messages d'erreur techniques en langage utilisateur
 */

/**
 * Traduit les erreurs réseau
 */
function translateNetworkError() {
  return {
    title: 'Problème de connexion',
    description: 'Impossible de se connecter au serveur. Vérifiez votre connexion internet et réessayez.'
  }
}

/**
 * Traduit les erreurs HTTP selon le code de statut
 */
function translateHttpError(status, action, resource, errorMessage = null) {
  // Une 422 porte souvent un message de validation précis (ex : règles de mot de passe) :
  // on le garde plutôt que d'afficher un texte générique
  const hasValidationDetail = errorMessage &&
    !errorMessage.startsWith('HTTP error') &&
    errorMessage !== 'Erreur de validation'

  const httpErrorMap = {
    404: { title: 'Ressource introuvable', description: `${resource} n'existe pas ou a été supprimée.` },
    403: { title: 'Accès refusé', description: `Vous n'avez pas les permissions nécessaires pour ${action}.` },
    401: { title: 'Session expirée', description: 'Votre session a expiré. Veuillez vous reconnecter.' },
    422: {
      title: 'Données invalides',
      description: hasValidationDetail
        ? errorMessage
        : 'Les informations saisies ne sont pas valides. Veuillez vérifier et réessayer.'
    }
  }
  
  if (httpErrorMap[status]) {
    return httpErrorMap[status]
  }
  
  if (status >= 500) {
    return {
      title: 'Erreur serveur',
      description: 'Une erreur est survenue côté serveur. Veuillez réessayer dans quelques instants.'
    }
  }
  
  return null
}

/**
 * Traduit les erreurs spécifiques par message
 */
function translateMessageError(errorMessage) {
  if (errorMessage.includes('timeout')) {
    return {
      title: 'Délai d\'attente dépassé',
      description: 'La requête a pris trop de temps. Veuillez réessayer.'
    }
  }
  
  if (errorMessage.includes('validation') || errorMessage.includes('invalide')) {
    return {
      title: 'Données invalides',
      description: errorMessage
    }
  }
  
  return null
}

/**
 * Traduit un message d'erreur technique en message utilisateur compréhensible
 * @param {Error|string} error - L'erreur ou le message d'erreur
 * @param {Object} context - Contexte supplémentaire (action, ressource, etc.)
 * @returns {Object} - { title: string, description: string }
 */
export function translateError(error, context = {}) {
  const errorMessage = typeof error === 'string' ? error : error.message || 'Une erreur est survenue'
  const errorStatus = error.response?.status || error.status
  const action = context.action || 'effectuer cette action'
  const resource = context.resource || 'la ressource'

  if (errorMessage.includes('Network Error') || errorMessage.includes('Failed to fetch')) {
    return translateNetworkError()
  }

  const httpError = translateHttpError(errorStatus, action, resource, errorMessage)
  if (httpError) return httpError

  const messageError = translateMessageError(errorMessage)
  if (messageError) return messageError

  return {
    title: 'Erreur',
    description: errorMessage
  }
}

/**
 * Vérifie si une erreur est liée au réseau
 */
export function isNetworkError(error) {
  const errorMessage = typeof error === 'string' ? error : error.message || ''
  return errorMessage.includes('Network Error') || 
         MESSAGES_FETCH_RESEAU.test(errorMessage) ||
         errorMessage.includes('timeout')
}

/**
 * Le libellé d'un `fetch` interrompu, qui change avec le navigateur : Chrome
 * « Failed to fetch », Firefox « NetworkError when attempting to fetch
 * resource. », Safari « Load failed ». Le client API le relance tel quel.
 */
const MESSAGES_FETCH_RESEAU = /Failed to fetch|NetworkError|Load failed/

/**
 * Le message à montrer à un visiteur d'écran public pour un appel refusé.
 * Une coupure réseau n'a pas de réponse du serveur : son libellé technique, en
 * anglais (« Failed to fetch »), cède la place au texte de repli en français.
 * Un refus du serveur garde son message.
 *
 * @param {Error|null|undefined} error
 * @param {string} repli - le texte en français de l'appelant
 * @returns {string}
 */
export function messagePourVisiteur(error, repli) {
  if (!error) return repli
  if (estCoupureReseau(error)) return repli
  return error.message || repli
}

/**
 * L'appel n'a pas atteint le serveur : aucun statut HTTP, et le libellé d'un
 * `fetch` interrompu. Un écran public qui s'ouvre sur une telle erreur ne doit
 * pas conclure que son lien est mort — le lien n'a pas été vérifié.
 *
 * @param {Error|null|undefined} error
 * @returns {boolean}
 */
export function estCoupureReseau(error) {
  return !!error && error.status == null && isNetworkError(error)
}

/**
 * Vérifie si une erreur est récupérable (peut être réessayée)
 */
export function isRetryableError(error) {
  const errorStatus = error.response?.status || error.status
  return isNetworkError(error) || 
         errorStatus >= 500 || 
         errorStatus === 429 // Too Many Requests
}
